import { BREAKPOINTS } from './templates/responsive';
import { DeviceKey } from './types';

/**
 * Screen-width ranges for the "Devices" display rule. They are the same
 * widths as the template's Screen settings (1366, 1280, 1024, 768, 640).
 */
export const DEVICES: {
	key: DeviceKey;
	label: string;
	/** Widths it covers, e.g. "1025–1280px". */
	range: string;
	device: 'desktop' | 'laptop' | 'tablet' | 'mobile';
}[] = [
	{
		key: 'desktop',
		label: 'Desktop',
		range: `${ BREAKPOINTS[ 0 ].width + 1 }px +`,
		device: 'desktop',
	},
	...BREAKPOINTS.map( ( bp, index ) => {
		const narrower = BREAKPOINTS[ index + 1 ];
		return {
			key: bp.key,
			label: String( bp.width ),
			range: narrower
				? `${ narrower.width + 1 }–${ bp.width }px`
				: `≤ ${ bp.width }px`,
			device: bp.device,
		};
	} ),
];

export const DEVICE_KEYS: DeviceKey[] = DEVICES.map( ( item ) => item.key );

/**
 * The range a screen width falls in.
 *
 * @param width Viewport width in px.
 */
export const getDeviceKey = ( width: number ): DeviceKey => {
	const match = [ ...BREAKPOINTS ]
		.reverse()
		.find( ( bp ) => width <= bp.width );
	return match ? match.key : 'desktop';
};

/** Every range on. */
export const allDevices = (): Record< DeviceKey, boolean > =>
	DEVICE_KEYS.reduce(
		( all, key ) => ( { ...all, [ key ]: true } ),
		{} as Record< DeviceKey, boolean >
	);

/**
 * Saved device rule. Old banners stored desktop / tablet / mobile; they map
 * to the new ranges (desktop = 1025px+, tablet = 769–1024px, mobile = ≤768px).
 *
 * @param raw Saved `devices` value.
 */
export const normalizeDevices = (
	raw: unknown
): Record< DeviceKey, boolean > => {
	const devices = allDevices();
	if ( ! raw || typeof raw !== 'object' ) {
		return devices;
	}
	const source = raw as Record< string, unknown >;
	const flag = ( value: unknown ) =>
		typeof value === 'boolean' ? value : value !== 'false' && !! value;

	const isLegacy =
		! ( 'w1366' in source ) && ( 'tablet' in source || 'mobile' in source );
	if ( isLegacy ) {
		const desktop = 'desktop' in source ? flag( source.desktop ) : true;
		const tablet = 'tablet' in source ? flag( source.tablet ) : true;
		const mobile = 'mobile' in source ? flag( source.mobile ) : true;
		return {
			desktop,
			w1366: desktop,
			w1280: desktop,
			w1024: tablet,
			w768: mobile,
			w640: mobile,
		};
	}
	DEVICE_KEYS.forEach( ( key ) => {
		if ( key in source ) {
			devices[ key ] = flag( source[ key ] );
		}
	} );
	return devices;
};
